// Step 23 — DealActions linked to a contract.
//
// Reuses `listDealActions` (Step 13) — no new endpoint. The backend
// exposes filtering by acquirer_id but not by contract_id, so we pull
// the workspace's actions and filter client-side to those whose
// `contract_draft_id` matches the current contract. If the payload
// doesn't carry `contract_draft_id` (older rows) we fall back to
// acquirer-scoped filtering when the contract has an acquirer.
//
// No approve/reject/start/complete/cancel/supersede path is exposed
// here — the intelligence workspace only reads, and links out to the
// existing actions page for human-controlled workflow.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listDealActions } from "../../api";
import { AIEmpty, AIError, AILoading, AISection } from "../AIPrimitives";

const STATUS_LABEL = {
  proposed: "Föreslagen",
  pending_review: "Under granskning",
  approved: "Godkänd",
  in_progress: "Pågår",
  blocked: "Blockerad",
  completed: "Klar",
  rejected: "Avvisad",
  cancelled: "Avbruten",
  superseded: "Ersatt",
};

export default function AIContractActions({ detail, workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const contractId = detail?.id;
  const acquirerId = detail?.acquirer_id;

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!workspaceId || !contractId) return;
      setLoading(true);
      setError(null);
      try {
        const params = { limit: 100 };
        if (acquirerId != null) params.acquirer_id = acquirerId;
        const res = await listDealActions(workspaceId, params, { signal });
        const rows = res?.results || [];
        // Prefer contract-scoped match when the row exposes it; otherwise
        // keep the acquirer-scoped rows (we already filtered server-side).
        const cScoped = rows.filter(
          (r) => r.contract_draft_id === contractId,
        );
        setItems(cScoped.length ? cScoped : rows);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda åtgärder.");
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, contractId, acquirerId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  return (
    <AISection
      title="Kopplade åtgärder"
      right={
        <Link
          to={`/deals/${workspaceId}/actions`}
          className="qai-page-section-link"
        >
          Alla åtgärder →
        </Link>
      }
    >
      <AIError message={error} />
      {loading && items.length === 0 ? (
        <AILoading />
      ) : items.length === 0 ? (
        <AIEmpty
          title="Inga åtgärder kopplade till detta utkast"
          hint={
            "Nya åtgärder kan föreslås av AI:n via `propose_deal_action`. " +
            "Godkännande sker manuellt i granskningsvyn."
          }
        />
      ) : (
        <ul className="qai-contract-actions-list">
          {items.map((a) => (
            <li key={a.id} className="qai-contract-action-row">
              <div className="qai-contract-action-main">
                <div className="qai-contract-action-title">{a.title}</div>
                <div className="qai-contract-action-meta">
                  <span className="qai-contract-action-status">
                    {STATUS_LABEL[a.status] || a.status}
                  </span>
                  {a.priority && (
                    <span className="qai-contract-action-prio">
                      · Prioritet: {a.priority}
                    </span>
                  )}
                  {a.source_kind && (
                    <span className="qai-contract-action-source">
                      · Källa: {a.source_kind}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/deals/${workspaceId}/actions`}
                className="qai-page-section-link"
              >
                Öppna →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AISection>
  );
}
